import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessVerificationContext = createContext();

export const useBusinessVerification = () => {
  const context = useContext(BusinessVerificationContext);
  if (!context) {
    throw new Error('useBusinessVerification must be used within a BusinessVerificationProvider');
  }
  return context;
};

export const BusinessVerificationProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // CHANGED: Simplified the base URL construction
  const getVerificationBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/verification`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/verification`;
    } else {
      throw new Error('Invalid login type for verification access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access verification management');
    }
  }, [loginType]);

  // Get verification status for a business by registry number
  const getVerificationStatus = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getVerificationBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get verification status';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getVerificationBaseUrl]);

  // Update verification status for a business
  const updateBusinessVerification = useCallback(async (registryNumber, verificationData) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    if (verificationData.verified_email === undefined && verificationData.verified_phone === undefined) {
      throw new Error('At least one verification field (verified_email or verified_phone) is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getVerificationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(verificationData),
        }
      );
      const result = await handleApiResponse(response);
      return result.verification_status;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update verification status';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getVerificationBaseUrl]);

  // Get verification statistics across all businesses
  const getVerificationStats = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getVerificationBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/stats`);
      const result = await handleApiResponse(response);
      return result.stats;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get verification statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getVerificationBaseUrl]);

  // Update email verification only
  const updateEmailVerification = useCallback(async (registryNumber, isVerified) => {
    validateAccess();

    return await updateBusinessVerification(registryNumber, {
      verified_email: isVerified,
    });
  }, [updateBusinessVerification, validateAccess]);

  // Update phone verification only
  const updatePhoneVerification = useCallback(async (registryNumber, isVerified) => {
    validateAccess();

    return await updateBusinessVerification(registryNumber, {
      verified_phone: isVerified,
    });
  }, [updateBusinessVerification, validateAccess]);

  // Update both email and phone verification
  const updateFullVerification = useCallback(async (registryNumber, emailVerified, phoneVerified) => {
    validateAccess();

    return await updateBusinessVerification(registryNumber, {
      verified_email: emailVerified,
      verified_phone: phoneVerified,
    });
  }, [updateBusinessVerification, validateAccess]);

  // Verify a business (set both to true)
  const verifyBusiness = useCallback(async (registryNumber) => {
    validateAccess();

    return await updateBusinessVerification(registryNumber, {
      verified_email: true,
      verified_phone: true,
    });
  }, [updateBusinessVerification, validateAccess]);

  // Unverify a business (set both to false)
  const unverifyBusiness = useCallback(async (registryNumber) => {
    validateAccess();

    return await updateBusinessVerification(registryNumber, {
      verified_email: false,
      verified_phone: false,
    });
  }, [updateBusinessVerification, validateAccess]);

  // CHANGED: Refactored to use the central getVerificationBaseUrl function
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const baseUrl = getVerificationBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/health`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, getVerificationBaseUrl]);

  // Format verification status for display
  const formatVerificationStatus = useCallback((status) => {
    const emailStatus = status.verified_email ? 'Verified' : 'Not Verified';
    const phoneStatus = status.verified_phone ? 'Verified' : 'Not Verified';
    const isFullyVerified = status.verified_email && status.verified_phone;
    const isPartiallyVerified = (status.verified_email || status.verified_phone) && !isFullyVerified;
    const isUnverified = !status.verified_email && !status.verified_phone;

    return {
      ...status,
      emailStatus,
      phoneStatus,
      emailColor: status.verified_email ? 'green' : 'red',
      phoneColor: status.verified_phone ? 'green' : 'red',
      overallStatus: isFullyVerified ? 'Fully Verified' : 
                     isPartiallyVerified ? 'Partially Verified' : 
                     'Unverified',
      overallColor: isFullyVerified ? 'green' : 
                    isPartiallyVerified ? 'yellow' : 
                    'red',
      isFullyVerified,
      isPartiallyVerified,
      isUnverified,
      verificationProgress: status.verified_email && status.verified_phone ? 100 :
                           status.verified_email || status.verified_phone ? 50 : 0,
    };
  }, []);

  // Format verification statistics for display
  const formatVerificationStats = useCallback((stats) => {
    const totalBusinesses = stats.total_businesses || 0;
    
    return {
      ...stats,
      verificationRate: totalBusinesses > 0 
        ? ((stats.fully_verified_count / totalBusinesses) * 100).toFixed(1) + '%'
        : '0%',
      emailVerificationRate: stats.email_verified_percent 
        ? stats.email_verified_percent.toFixed(1) + '%'
        : '0%',
      phoneVerificationRate: stats.phone_verified_percent 
        ? stats.phone_verified_percent.toFixed(1) + '%'
        : '0%',
      unverifiedRate: totalBusinesses > 0 
        ? ((stats.unverified_count / totalBusinesses) * 100).toFixed(1) + '%'
        : '0%',
      hasBusinesses: totalBusinesses > 0,
      needsAttention: stats.unverified_count > 0,
    };
  }, []);

  // Get verification summary for a business
  const getVerificationSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const status = await getVerificationStatus(registryNumber);
      const formatted = formatVerificationStatus(status);

      return {
        registryNumber: status.business_registry_number,
        businessName: status.business_name,
        email: status.business_email,
        phone: status.business_phone,
        ...formatted,
        accessLevel: loginType,
        canModify: loginType === 'admin' || loginType === 'master',
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get verification summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getVerificationStatus, formatVerificationStatus, validateAccess, loginType]);

  // Validate verification data
  const validateVerificationData = useCallback((data) => {
    const errors = [];

    if (data.verified_email !== undefined && typeof data.verified_email !== 'boolean') {
      errors.push({ field: 'verified_email', message: 'Email verification must be a boolean value' });
    }

    if (data.verified_phone !== undefined && typeof data.verified_phone !== 'boolean') {
      errors.push({ field: 'verified_phone', message: 'Phone verification must be a boolean value' });
    }

    if (data.verified_email === undefined && data.verified_phone === undefined) {
      errors.push({ field: 'general', message: 'At least one verification field must be provided' });
    }

    return errors;
  }, []);

  // Batch update verification status for multiple businesses
  const batchUpdateVerification = useCallback(async (updates) => {
    validateAccess();

    if (!Array.isArray(updates) || updates.length === 0) {
      throw new Error('Updates must be a non-empty array');
    }

    const results = {
      successful: [],
      failed: [],
      total: updates.length,
    };

    setLoading(true);
    setError(null);

    try {
      for (const update of updates) {
        try {
          const result = await updateBusinessVerification(
            update.registryNumber,
            update.verificationData
          );
          results.successful.push({
            registryNumber: update.registryNumber,
            result,
          });
        } catch (err) {
          results.failed.push({
            registryNumber: update.registryNumber,
            error: err.message,
          });
        }
      }

      return results;
    } catch (err) {
      const errorMessage = err.message || 'Batch update failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [updateBusinessVerification, validateAccess]);

  // CHANGED: Updated debugging info to reflect the new, simpler routes
  const getEndpointInfo = useCallback(() => {
    const baseUrl = getVerificationBaseUrl();
    return {
      loginType,
      baseUrl: baseUrl,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
      availableEndpoints: [
        'GET /registry/{registryNumber}',
        'PATCH /registry/{registryNumber}',
        'GET /stats',
        'GET /health',
      ],
    };
  }, [loginType, getVerificationBaseUrl]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getVerificationStatus,
    updateBusinessVerification,
    getVerificationStats,
    
    // Specific Update Actions
    updateEmailVerification,
    updatePhoneVerification,
    updateFullVerification,
    verifyBusiness,
    unverifyBusiness,
    
    // Utility Actions
    checkHealth,
    getVerificationSummary,
    batchUpdateVerification,
    
    // Helpers
    clearError,
    formatVerificationStatus,
    formatVerificationStats,
    validateVerificationData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasVerificationAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessVerificationContext.Provider value={value}>
      {children}
    </BusinessVerificationContext.Provider>
  );
};